import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'
import { getRepository } from '@things-factory/shell'
import type ResolverContext from '@things-factory/auth-base'

import { KpiScope } from './kpi-scope'
import { NewKpiScope, KpiScopePatch } from './kpi-scope-type'

@Resolver(KpiScope)
export class KpiScopeMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiScope, { description: 'Create a new scope dimension definition' })
  async createKpiScope(
    @Arg('kpiScope') kpiScope: NewKpiScope,
    @Ctx() context: ResolverContext
  ): Promise<KpiScope> {
    const { domain, user, tx } = context.state

    const result = await getRepository(KpiScope, tx).save({
      ...kpiScope,
      domain,
      creator: user,
      updater: user
    })

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiScope, { description: 'Update a scope dimension definition' })
  async updateKpiScope(
    @Arg('id') id: string,
    @Arg('patch') patch: KpiScopePatch,
    @Ctx() context: ResolverContext
  ): Promise<KpiScope> {
    const { domain, user, tx } = context.state

    const repository = getRepository(KpiScope, tx)
    const kpiScope = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    const result = await repository.save({
      ...kpiScope,
      ...patch,
      updater: user
    })

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiScope], { description: "Update multiple scope dimension definitions" })
  async updateMultipleKpiScope(
    @Arg('patches', type => [KpiScopePatch]) patches: KpiScopePatch[],
    @Ctx() context: ResolverContext
  ): Promise<KpiScope[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const kpiScopeRepo = getRepository(KpiScope, tx)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await kpiScopeRepo.save({
          ...newRecord,
          domain,
          creator: user,
          updater: user
        })

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const kpiScope = await kpiScopeRepo.findOne({
          where: { domain: { id: domain.id }, id: updateRecord.id }
        })

        if (!kpiScope) {
          throw new Error(`failed to find kpiScope to update: ${updateRecord.id}`)
        }

        const result = await kpiScopeRepo.save({
          ...kpiScope,
          ...updateRecord,
          updater: user
        })

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'Delete a scope dimension definition' })
  async deleteKpiScope(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(KpiScope, tx).delete({ domain: { id: domain.id }, id })

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'Delete multiple scope dimension definitions' })
  async deleteKpiScopes(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(KpiScope, tx).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    return true
  }

}